import { html, nothing } from "lit";
import type {
  RecordSkillDetail,
  RecordSkillPrivacyNotice,
  RecordSkillRow,
} from "../controllers/record-skill.ts";

export type RecordSkillPanelProps = {
  loading: boolean;
  sessions: RecordSkillRow[];
  error: string | null;
  privacy: RecordSkillPrivacyNotice | null;
  activeId?: string | null;
  selectedId?: string | null;
  detail?: RecordSkillDetail | null;
  desktopNodeOptions?: Array<{ url: string; label: string }>;
  onRefresh: () => void;
  onLoadPrivacy: () => void;
  onStart: (input: {
    title: string;
    desktopControllerUrl?: string;
    narrationTranscript?: string;
    privacyAcknowledged: boolean;
  }) => void;
  onStop: (recordId: string, narrationTranscript?: string) => void;
  onFinalize: (recordId: string, narrationTranscript?: string) => void;
  onSelect: (recordId: string | null) => void;
};

export function renderRecordSkillPanel(props: RecordSkillPanelProps) {
  const privacy = props.privacy;
  return html`
    <section class="card" style="margin-top: 24px;">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <div>
          <h3 style="margin: 0;">Record a Skill</h3>
          <p class="muted" style="margin: 4px 0 0; font-size: 13px;">
            Capture a desktop workflow (+ optional narration), synthesize an AgentSkill, publish for
            agents to re-run.
          </p>
        </div>
        <button class="btn" type="button" @click=${props.onRefresh} ?disabled=${props.loading}>
          Refresh
        </button>
      </div>

      ${props.error ? html`<p class="error" style="margin-top: 12px;">${props.error}</p>` : nothing}

      <div
        style="margin-top: 16px; padding: 12px; border: 1px solid var(--border, #333); border-radius: 8px; background: var(--bg-elevated, transparent);"
      >
        <div class="row" style="justify-content: space-between; align-items: center;">
          <strong>${privacy?.title ?? "Privacy notice"}</strong>
          <button class="btn btn--sm" type="button" @click=${props.onLoadPrivacy}>Show</button>
        </div>
        ${
          privacy
            ? html`
                <ul style="margin: 8px 0 0; padding-left: 18px; font-size: 13px;">
                  ${privacy.bullets.map((b) => html`<li>${b}</li>`)}
                </ul>
              `
            : html`
                <p class="muted" style="font-size: 12px; margin: 8px 0 0">
                  Load the privacy notice before recording. Never type passwords while capturing.
                </p>
              `
        }
      </div>

      <form
        style="margin-top: 16px; display: grid; gap: 8px;"
        @submit=${(e: Event) => {
          e.preventDefault();
          const form = e.target as HTMLFormElement;
          const data = new FormData(form);
          const title = String(data.get("title") ?? "").trim();
          const desktopControllerUrl = String(data.get("desktopControllerUrl") ?? "").trim();
          const narrationTranscript = String(data.get("narration") ?? "").trim();
          const privacyAcknowledged = data.get("privacyAcknowledged") === "on";
          props.onStart({
            title: title || "Recorded skill",
            desktopControllerUrl: desktopControllerUrl || undefined,
            narrationTranscript: narrationTranscript || undefined,
            privacyAcknowledged,
          });
        }}
      >
        <label>
          Title
          <input name="title" type="text" placeholder="e.g. Create sales invoice" required />
        </label>
        <label>
          Desktop controller URL (optional if a node is already wired)
          <input
            name="desktopControllerUrl"
            type="text"
            list="record-skill-desktop-urls"
            placeholder="http://192.168.x.x:8765"
          />
          <datalist id="record-skill-desktop-urls">
            ${(props.desktopNodeOptions ?? []).map(
              (n) => html`<option value=${n.url}>${n.label}</option>`,
            )}
          </datalist>
        </label>
        <label>
          Narration / notes (optional)
          <textarea
            name="narration"
            rows="3"
            placeholder="Speak or type decision points: If X then Y. Otherwise Z."
          ></textarea>
        </label>
        <label class="row" style="gap: 8px; align-items: center;">
          <input name="privacyAcknowledged" type="checkbox" required />
          <span>${privacy?.acknowledgeLabel ?? "I understand the privacy risks"}</span>
        </label>
        <div class="row" style="gap: 8px;">
          <button class="btn btn--primary" type="submit" ?disabled=${Boolean(props.activeId)}>
            Start recording
          </button>
          ${
            props.activeId
              ? html`
                  <button
                    class="btn"
                    type="button"
                    @click=${() => {
                      const narration = (
                        document.querySelector(
                          'textarea[name="narration"]',
                        ) as HTMLTextAreaElement | null
                      )?.value;
                      props.onStop(props.activeId!, narration || undefined);
                    }}
                  >
                    Stop
                  </button>
                  <button
                    class="btn btn--primary"
                    type="button"
                    @click=${() => {
                      const narration = (
                        document.querySelector(
                          'textarea[name="narration"]',
                        ) as HTMLTextAreaElement | null
                      )?.value;
                      props.onFinalize(props.activeId!, narration || undefined);
                    }}
                  >
                    Stop & publish skill
                  </button>
                `
              : nothing
          }
        </div>
      </form>

      <div style="margin-top: 20px;">
        <h4 style="margin: 0 0 8px;">Sessions</h4>
        ${
          props.loading
            ? html`
                <p class="muted">Loading…</p>
              `
            : props.sessions.length === 0
              ? html`
                  <p class="muted">No recordings yet.</p>
                `
              : html`
                  <ul style="list-style: none; padding: 0; margin: 0; display: grid; gap: 8px;">
                    ${props.sessions.map(
                      (s) => html`
                        <li>
                          <button
                            class="btn"
                            type="button"
                            style="width: 100%; text-align: left;"
                            @click=${() => props.onSelect(s.recordId)}
                          >
                            <strong>${s.title}</strong>
                            <span class="muted"> · ${s.status}</span>
                            ${
                              s.eventCount != null
                                ? html`<span class="muted"> · ${s.eventCount} events</span>`
                                : nothing
                            }
                            ${
                              s.skillName
                                ? html`<span class="muted"> · ${s.skillName}</span>`
                                : nothing
                            }
                          </button>
                        </li>
                      `,
                    )}
                  </ul>
                `
        }
      </div>

      ${props.detail ? renderDetail(props.detail) : nothing}
    </section>
  `;
}

function renderDetail(detail: RecordSkillDetail) {
  return html`
    <div style="margin-top: 16px; padding: 12px; border: 1px solid var(--border, #333); border-radius: 8px;">
      <h4 style="margin: 0 0 8px;">${detail.title}</h4>
      <p class="muted" style="font-size: 12px; margin: 0 0 8px;">
        ${detail.status}
        ${detail.publishedPath ? html` · published: <code>${detail.publishedPath}</code>` : nothing}
      </p>
      ${
        detail.privacyNotes?.length
          ? html`
              <ul style="font-size: 12px;">
                ${detail.privacyNotes.map((n) => html`<li>${n}</li>`)}
              </ul>
            `
          : nothing
      }
      ${
        detail.draftPreview
          ? html`
              <pre
                style="max-height: 280px; overflow: auto; font-size: 11px; white-space: pre-wrap;"
              >${detail.draftPreview}</pre>
            `
          : nothing
      }
    </div>
  `;
}
